import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { useAuth } from '@context/AuthContext';

interface Match {
  id: string;
  users: [string, string];
  compatibilityScore: number;
  budgetOverlap: {
    min: number;
    max: number;
  };
  sharedCity: string;
  sharedLifestyle: string[];
  viewedProperties: string[];
  savedProperties: string[];
  matchedAt: Timestamp;
  lastMessageAt?: Timestamp;
  unreadCount?: { [userId: string]: number };
  isActive: boolean;
}

export const useMatches = () => {
  const { currentUser } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [indexBuilding, setIndexBuilding] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    // Primary query (requires composite index that may still be building)
    const matchesQuery = query(
      collection(firestore, 'matches'),
      where('users', 'array-contains', currentUser.uid),
      where('isActive', '==', true),
      orderBy('matchedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      matchesQuery,
      (snapshot) => {
        const matchesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Match[];
        
        setMatches(matchesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching matches:', err);
        // Detect index building state
        if (err.code === 'failed-precondition' && /index.*building/i.test(err.message)) {
          setIndexBuilding(true);
          setError('INDEX_BUILDING');
        } else if (err.code === 'permission-denied') {
          setError('PERMISSION_DENIED');
        } else {
          setError('FAILED');
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  return { matches, loading, error, indexBuilding };
};
